import { create } from 'zustand';
import type { BoardState, HarmonyReport, PuzzleDefinition, StrikeResult } from '@/types';
import { strike } from '@/engine/propagation';
import { analyze } from '@/engine/harmony';
import { calculateScore } from '@/engine/score';
import { calculateStars } from '@/engine/stars';
import type { ColorBlindMode } from '@/accessibility/colorblind';
import {
  loadProgress,
  saveProgress,
  recordPuzzleSolve,
  recordDailySolve,
  type PlayerProgress,
} from '@/engine/progression';
import {
  getDailyPuzzleId,
  formatDateKey,
  updateStreak,
} from '@/engine/daily';
import { getPuzzle } from '@/engine/loader';
import {
  submitScore as sdkSubmitScore,
  completeDailyChallenge as sdkCompleteDailyChallenge,
} from '@/sdk/funway';
import { hasSeenChapterIntro } from '@/utils/tutorialStorage';

export type Screen = 'menu' | 'puzzleSelect' | 'game';

export interface GameStore {
  // Navigation
  screen: Screen;
  currentChapter: number;

  // Board state
  board: BoardState | null;
  initialBoard: BoardState | null;
  undoStack: BoardState[];

  // Puzzle
  puzzle: PuzzleDefinition | null;

  // Game state
  strikesUsed: number;
  strikeBudget: number;
  undoAvailable: boolean;
  undoUsedThisPuzzle: boolean;
  resetsUsed: number;
  startTime: number;
  isComplete: boolean;
  isLost: boolean;

  // Harmony
  harmonyReport: HarmonyReport | null;

  // Animation
  lastStrikeResult: StrikeResult | null;

  // Accessibility settings
  colorBlindMode: ColorBlindMode;
  reducedMotion: boolean;
  showFrequencyNumbers: boolean;

  // Settings UI
  settingsOpen: boolean;

  // Progress
  progress: PlayerProgress;

  // Daily
  dailyPuzzleId: string | null;
  isDailyMode: boolean;

  // Hint system
  hintLevel: number;         // 0 = no hint, 1 = first node shown, 2 = full sequence shown
  hintUsed: boolean;         // prevents 3-star rating
  hintNodeIds: string[];     // the optimal solution node sequence
  puzzleStartedAt: number;   // timestamp when puzzle was loaded
  lastActionAt: number;      // updated on strike, undo, reset

  // Practice mode
  practiceMode: boolean;

  // Practice select
  isPracticeSelect: boolean;

  // Tutorial
  showTutorial: boolean;
  showChapterIntro: boolean;

  // Actions
  navigateTo: (screen: Screen) => void;
  setChapter: (chapter: number) => void;
  loadPuzzle: (puzzle: PuzzleDefinition) => void;
  loadPuzzlePractice: (puzzle: PuzzleDefinition) => void;
  strikeNode: (nodeId: string) => void;
  undo: () => void;
  reset: () => void;
  setColorBlindMode: (mode: ColorBlindMode) => void;
  setReducedMotion: (enabled: boolean) => void;
  setShowFrequencyNumbers: (show: boolean) => void;
  setSettingsOpen: (open: boolean) => void;
  loadDailyChallenge: () => void;
  recordSolve: () => void;
  requestHint: () => void;
  enterPractice: () => void;
  tutorialComplete: () => void;
  chapterIntroComplete: () => void;
}

/** Extract hint node IDs from puzzle solutions. */
function extractHintNodeIds(puzzle: PuzzleDefinition): string[] {
  const firstSolution = puzzle.solutions[0];
  if (!firstSolution) return [];
  return [...firstSolution.strikes];
}

export const useGameStore = create<GameStore>((set, get) => ({
  screen: 'menu',
  currentChapter: 1,
  board: null,
  initialBoard: null,
  undoStack: [],
  puzzle: null,
  strikesUsed: 0,
  strikeBudget: 0,
  undoAvailable: false,
  undoUsedThisPuzzle: false,
  resetsUsed: 0,
  startTime: 0,
  isComplete: false,
  isLost: false,
  harmonyReport: null,
  lastStrikeResult: null,
  colorBlindMode: 'none',
  reducedMotion: typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches || false,
  showFrequencyNumbers: true,
  settingsOpen: false,

  // Load progress from localStorage on initialization
  progress: loadProgress(),
  dailyPuzzleId: null,
  isDailyMode: false,

  // Hint system defaults
  hintLevel: 0,
  hintUsed: false,
  hintNodeIds: [],
  puzzleStartedAt: 0,
  lastActionAt: 0,

  // Practice mode defaults
  practiceMode: false,
  isPracticeSelect: false,

  // Tutorial defaults
  showTutorial: typeof window !== 'undefined' && !localStorage.getItem('resonance_onboarding_done'),
  showChapterIntro: false,

  navigateTo: (screen) => set({ screen }),
  setChapter: (chapter) => set({ currentChapter: chapter }),

  loadPuzzle: (puzzle) => {
    const board = structuredClone(puzzle.board);
    const harmonyReport = analyze(board);
    const now = Date.now();

    // Check if this is the first puzzle of a new chapter (ch > 1, index 1)
    const chapterMatch = puzzle.id.match(/ch(\d+)-(\d+)$/);
    const puzzleChapter = chapterMatch?.[1] ? parseInt(chapterMatch[1], 10) : 0;
    const puzzleIndex = chapterMatch?.[2] ? parseInt(chapterMatch[2], 10) : 0;
    const needsChapterIntro =
      puzzleChapter > 1 && puzzleIndex === 1 && !hasSeenChapterIntro(puzzleChapter);

    set({
      puzzle,
      board,
      initialBoard: structuredClone(puzzle.board),
      undoStack: [],
      strikesUsed: 0,
      strikeBudget: puzzle.constraints.strikeBudget,
      undoAvailable: false,
      undoUsedThisPuzzle: false,
      resetsUsed: 0,
      startTime: now,
      isComplete: false,
      isLost: false,
      harmonyReport,
      lastStrikeResult: null,
      screen: 'game',
      isDailyMode: false,
      practiceMode: false,
      isPracticeSelect: false,
      showChapterIntro: needsChapterIntro,
      // Hint system reset
      hintLevel: 0,
      hintUsed: false,
      hintNodeIds: extractHintNodeIds(puzzle),
      puzzleStartedAt: now,
      lastActionAt: now,
    });
  },

  loadPuzzlePractice: (puzzle) => {
    const board = structuredClone(puzzle.board);
    const harmonyReport = analyze(board);
    const now = Date.now();
    set({
      puzzle,
      board,
      initialBoard: structuredClone(puzzle.board),
      undoStack: [],
      strikesUsed: 0,
      strikeBudget: puzzle.constraints.strikeBudget,
      undoAvailable: false,
      undoUsedThisPuzzle: false,
      resetsUsed: 0,
      startTime: now,
      isComplete: false,
      isLost: false,
      harmonyReport,
      lastStrikeResult: null,
      screen: 'game',
      isDailyMode: false,
      practiceMode: true,
      isPracticeSelect: false,
      // Hint system reset
      hintLevel: 0,
      hintUsed: false,
      hintNodeIds: extractHintNodeIds(puzzle),
      puzzleStartedAt: now,
      lastActionAt: now,
    });
  },

  strikeNode: (nodeId) => {
    const { board, strikesUsed, strikeBudget, isComplete, isLost, practiceMode, undoStack } = get();
    if (!board || isComplete || isLost) return;
    if (strikesUsed >= strikeBudget) return;

    // Check if node is locked
    const node = board.nodes.find((n) => n.id === nodeId);
    if (!node || node.locked) return;

    const result = strike(board, nodeId);
    const harmonyReport = analyze(result.newBoard);
    const newStrikesUsed = strikesUsed + 1;
    const complete = harmonyReport.resonancePercentage === 100;
    const lost = !complete && newStrikesUsed >= strikeBudget;

    // In practice mode, keep full undo stack; in normal mode, limit to 1
    const newUndoStack = practiceMode
      ? [...undoStack, structuredClone(board)]
      : [structuredClone(board)];

    set({
      undoStack: newUndoStack,
      board: result.newBoard,
      strikesUsed: newStrikesUsed,
      undoAvailable: true,
      isComplete: complete,
      isLost: lost,
      harmonyReport,
      lastStrikeResult: result,
      lastActionAt: Date.now(),
    });
  },

  undo: () => {
    const { undoStack, undoAvailable, strikesUsed, isComplete } = get();
    if (undoStack.length === 0 || !undoAvailable) return;
    // Don't allow undo after game end
    if (isComplete) return;

    const newStack = [...undoStack];
    const prevBoard = newStack.pop();
    if (!prevBoard) return;

    const harmonyReport = analyze(prevBoard);
    set({
      board: prevBoard,
      undoStack: newStack,
      strikesUsed: strikesUsed - 1,
      undoAvailable: newStack.length > 0,
      undoUsedThisPuzzle: true,
      isLost: false,
      harmonyReport,
      lastStrikeResult: null,
      lastActionAt: Date.now(),
    });
  },

  reset: () => {
    const { initialBoard, resetsUsed, puzzle } = get();
    if (!initialBoard) return;

    const board = structuredClone(initialBoard);
    const harmonyReport = analyze(board);
    const now = Date.now();
    set({
      board,
      undoStack: [],
      strikesUsed: 0,
      undoAvailable: false,
      resetsUsed: resetsUsed + 1,
      isComplete: false,
      isLost: false,
      harmonyReport,
      lastStrikeResult: null,
      lastActionAt: now,
      // Reset hint level on reset but keep hintUsed flag
      hintLevel: 0,
      hintNodeIds: puzzle ? extractHintNodeIds(puzzle) : [],
    });
  },

  loadDailyChallenge: () => {
    const today = new Date();
    const puzzleId = getDailyPuzzleId(today);
    if (!puzzleId) return;

    const puzzle = getPuzzle(puzzleId);
    if (!puzzle) return;

    const board = structuredClone(puzzle.board);
    const harmonyReport = analyze(board);
    const now = Date.now();

    set({
      puzzle,
      board,
      initialBoard: structuredClone(puzzle.board),
      undoStack: [],
      strikesUsed: 0,
      strikeBudget: puzzle.constraints.strikeBudget,
      undoAvailable: false,
      undoUsedThisPuzzle: false,
      resetsUsed: 0,
      startTime: now,
      isComplete: false,
      isLost: false,
      harmonyReport,
      lastStrikeResult: null,
      screen: 'game',
      dailyPuzzleId: puzzleId,
      isDailyMode: true,
      practiceMode: false,
      isPracticeSelect: false,
      // Hint system reset
      hintLevel: 0,
      hintUsed: false,
      hintNodeIds: extractHintNodeIds(puzzle),
      puzzleStartedAt: now,
      lastActionAt: now,
    });
  },

  recordSolve: () => {
    const {
      puzzle,
      isComplete,
      isDailyMode,
      strikesUsed,
      strikeBudget,
      undoUsedThisPuzzle,
      resetsUsed,
      startTime,
      progress,
      hintUsed,
      practiceMode,
    } = get();

    if (!isComplete || !puzzle) return;

    // In practice mode, skip saving to progression
    if (practiceMode) return;

    const solveTimeSeconds = Math.round((Date.now() - startTime) / 1000);
    const targetTimeSeconds = 120;

    const score = calculateScore({
      tier: puzzle.tier,
      strikesUsed,
      strikeBudget,
      undoUsed: undoUsedThisPuzzle,
      solveTimeSeconds,
      targetTimeSeconds,
      resetsUsed,
    });

    let stars = calculateStars(
      true,
      strikesUsed,
      puzzle.constraints.minimumStrikes,
      undoUsedThisPuzzle,
      resetsUsed,
    );

    // If hint was used, cap stars at 2
    if (hintUsed && stars > 2) {
      stars = 2;
    }

    let newProgress: PlayerProgress;

    if (isDailyMode) {
      const today = formatDateKey(new Date());
      newProgress = recordDailySolve(progress, today, score.total, strikesUsed, stars);
      newProgress = updateStreak(newProgress, today);
    } else {
      newProgress = recordPuzzleSolve(progress, puzzle.id, score.total, stars, strikesUsed);
    }

    saveProgress(newProgress);
    set({ progress: newProgress });

    // Submit score to Funway SDK leaderboard
    sdkSubmitScore(score.total).catch(() => {});

    // If daily mode, mark daily complete in SDK
    if (isDailyMode) {
      sdkCompleteDailyChallenge().catch(() => {});
    }
  },

  requestHint: () => {
    const { hintLevel, hintNodeIds } = get();
    if (hintNodeIds.length === 0) return;

    if (hintLevel === 0) {
      set({ hintLevel: 1, hintUsed: true });
    } else if (hintLevel === 1) {
      set({ hintLevel: 2 });
    }
    // hintLevel === 2: no-op
  },

  enterPractice: () => {
    set({ isPracticeSelect: true, screen: 'puzzleSelect' });
  },

  tutorialComplete: () => {
    try {
      localStorage.setItem('resonance_onboarding_done', '1');
    } catch { /* ignore */ }
    set({ showTutorial: false });
  },

  chapterIntroComplete: () => {
    set({ showChapterIntro: false });
  },

  setColorBlindMode: (mode) => set({ colorBlindMode: mode }),
  setReducedMotion: (enabled) => set({ reducedMotion: enabled }),
  setShowFrequencyNumbers: (show) => set({ showFrequencyNumbers: show }),
  setSettingsOpen: (open) => set({ settingsOpen: open }),
}));
